import React from 'react';
import { Modal } from 'react-bootstrap';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { editGroup } from '../../actions/roleActions';
import {
  endLoading,
  startLoading,
} from '../../reducers/loading/loading.action';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import StylishNewInput from '../DesignSystems/New/StylishNewInput';
import validateEmailAddress from '@/utils/validateEmailAddress';

type EditGroupModalProps = {
  group: any;
  onClose: () => void;
  startDelete: () => void;
  show: boolean;
};
const EditGroupModal: React.FC<EditGroupModalProps> = ({
  group,
  onClose,
  startDelete,
  show,
}) => {
  const dispatch = useDispatch();

  const currentlySelectedGroup = useSelector(
    (s: any) => s.app.currentlySelectedGroup
  );
  const RBACPermissions = useSelector((state: any) => {
    return state.app.validateRBACPermissionForActionResult;
  });

  const {
    resetField,
    handleSubmit,
    control,
    formState: { errors, isValid, isSubmitted },
  } = useForm({ mode: 'onChange' });

  const onEditGroupSubmit: SubmitHandler<any> = async ({
    editGroupName,
    default_sender_email,
    arcgis_portal_url,
    oauth_client_id,
  }) => {
    dispatch(startLoading());
    const existingGroup = { ...group };
    existingGroup.key = editGroupName;
    existingGroup.group_guid = group.value;
    existingGroup.group_name = editGroupName;
    existingGroup.daily_digest_team = group.dailyDigestTeam;
    existingGroup.default_sender_email = default_sender_email;
    existingGroup.arcgis_portal_url = arcgis_portal_url;
    existingGroup.oauth_client_id = oauth_client_id;

    await dispatch(editGroup(existingGroup));
    dispatch(endLoading());
    onClose();
  };

  const onCloseRequest = () => {
    resetField('editGroupName');
    resetField('default_sender_email');
    resetField('arcgis_portal_url');
    resetField('oauth_client_id');
    onClose();
  };

  return (
    <Modal show={show} onHide={onCloseRequest} centered>
      <form onSubmit={handleSubmit(onEditGroupSubmit)}>
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>Edit Organization</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Name</label>
            {/* @ts-ignore */}
            <Controller
              control={control}
              name="editGroupName"
              rules={{ required: true }}
              defaultValue={currentlySelectedGroup.group_name}
              render={({ field: { onChange, value } }) => (
                <StylishNewInput
                  type="text"
                  onChange={(e: any) => onChange(e.target.value)}
                  value={value}
                />
              )}
            />
            {errors.editGroupName?.type === 'required' && (
              <span className="form-text form-error">
                This field is required
              </span>
            )}
            {errors.editGroupName?.type === 'custom' && (
              <span className="form-text form-error">
                This Group name is in use. Please enter a new one.
              </span>
            )}
          </div>
          <div className="mb-3">
            <label className="form-label">Organization From Email</label>
            <Controller
              control={control}
              name="default_sender_email"
              rules={{
                validate: (v) =>
                  !v || validateEmailAddress(v) || 'Invalid email address',
              }}
              defaultValue={currentlySelectedGroup.default_sender_email}
              render={({ field: { onChange, value } }) => (
                <StylishNewInput
                  type="text"
                  onChange={(e: any) => onChange(e.target.value)}
                  value={value}
                />
              )}
            />
            {errors.default_sender_email?.type === 'validate' && (
              <span className="form-text form-error">
                Please Enter a Valid Email
              </span>
            )}
          </div>
          <div className="mb-3">
            <label className="form-label">ArcGIS Portal URL</label>
            <Controller
              control={control}
              name="arcgis_portal_url"
              rules={{ required: false }}
              defaultValue={currentlySelectedGroup.arcgis_portal_url}
              render={({ field: { onChange, value } }) => (
                <StylishNewInput
                  type="text"
                  onChange={(e) => onChange(e.target.value)}
                  value={value}
                />
              )}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">OAuth Client ID</label>
            <Controller
              control={control}
              name="oauth_client_id"
              rules={{ required: false }}
              defaultValue={currentlySelectedGroup.oauth_client_id}
              render={({ field: { onChange, value } }) => (
                <StylishNewInput
                  type="text"
                  onChange={(e) => onChange(e.target.value)}
                  value={value}
                />
              )}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="button-group">
            <StylishNewButton
              type="button"
              className="button--error"
              onClick={startDelete}
              disabled={!RBACPermissions['Open or Close Group']}
            >
              Delete this Organization
            </StylishNewButton>
            <StylishNewButton
              type="button"
              className="button--secondary"
              onClick={onCloseRequest}
            >
              Cancel
            </StylishNewButton>
            <StylishNewButton
              type="submit"
              className="button--primary"
              disabled={isSubmitted && !isValid}
            >
              Save Organization
            </StylishNewButton>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default EditGroupModal;
